import { useLocalStorage } from '@vueuse/core';
import type { RemovableRef } from '@vueuse/core';

export type UserType = 'employee' | 'customer';

const EMPLOYEE_TOKEN_KEY = 'employee_auth_token';
const CUSTOMER_TOKEN_KEY = 'customer_auth_token';

const employeeTokenRef: RemovableRef<string | null> = useLocalStorage<string | null>(EMPLOYEE_TOKEN_KEY, null);
const customerTokenRef: RemovableRef<string | null> = useLocalStorage<string | null>(CUSTOMER_TOKEN_KEY, null);

class TokenManager {
  setEmployeeToken(token: string): void {
    employeeTokenRef.value = token;
  }

  setCustomerToken(token: string): void {
    customerTokenRef.value = token;
  }

  getEmployeeToken(): string | null {
    return employeeTokenRef.value;
  }

  getCustomerToken(): string | null {
    return customerTokenRef.value;
  }

  clearEmployeeToken(): void {
    employeeTokenRef.value = null;
  }

  clearCustomerToken(): void {
    customerTokenRef.value = null;
  }

  clearAllTokens(): void {
    this.clearEmployeeToken();
    this.clearCustomerToken();
  }

  getTokenForPath(path: string): string | null {
    if (path.startsWith('/admin')) {
      return this.getEmployeeToken();
    }

    if (
      path.startsWith('/company/') ||
      path.startsWith('/security/') ||
      path.startsWith('/select-company') ||
      path.startsWith('/pending-invitations') ||
      path === '/login' ||
      path === '/register'
    ) {
      return this.getCustomerToken();
    }

    return this.getCustomerToken() || this.getEmployeeToken();
  }

  getLoginRedirectForPath(path: string): string {
    if (path.startsWith('/admin')) {
      return '/admin/login';
    }

    return '/login';
  }

  hasAnyToken(): boolean {
    return !!(this.getEmployeeToken() || this.getCustomerToken());
  }

  isEmployeeAuthenticated(): boolean {
    return !!this.getEmployeeToken();
  }

  isCustomerAuthenticated(): boolean {
    return !!this.getCustomerToken();
  }

  getUserTypeFromPath(path: string): UserType | null {
    if (path.startsWith('/admin')) {
      return 'employee';
    }
    if (
      path.startsWith('/company/') ||
      path.startsWith('/security/') ||
      path.startsWith('/select-company') ||
      path.startsWith('/pending-invitations') ||
      path === '/login' ||
      path === '/register'
    ) {
      return 'customer';
    }

    return null;
  }
}

export const tokenManager = new TokenManager();
